import { useState, useRef } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';

interface UploadResult {
  success: boolean;
  rowsParsed?: number;
  rowsSaved?: number;
  rowsSkipped?: number;
  message?: string;
  error?: string;
}

interface YoycolCostUploadProps {
  isAdmin?: boolean;
}

/**
 * Admin-only upload for the daily Yoycol order/cost CSV export.
 *
 * Export from Yoycol as CSV (not .numbers — .numbers can't be parsed
 * reliably server-side). Upload is safe to repeat: the backend upserts
 * by Yoycol order ID, so re-uploading the same file (or an overlapping
 * one) just overwrites matching rows rather than duplicating them.
 */
export default function YoycolCostUpload({ isAdmin }: YoycolCostUploadProps) {
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<UploadResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Defense in depth: this panel is only meant for admins. The parent
  // already gates the surrounding section, but this mirrors how the
  // sibling sync buttons in this same panel are set up.
  if (!isAdmin) {
    return null;
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setStatus('uploading');
    setResult(null);

    try {
      const csvText = await file.text();

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be signed in to upload cost data.');
      }
      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/upload-yoycol-costs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ csvText }),
      });

      const data: UploadResult = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setResult(data);
      setStatus('done');
    } catch (err) {
      setResult({ success: false, error: err instanceof Error ? err.message : 'Upload failed' });
      setStatus('error');
    } finally {
      // Reset the input so the same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900">Yoycol cost data</h3>
      <p className="mt-1 text-sm text-gray-500">
        Upload today's order export from Yoycol as CSV. Re-uploading is safe —
        matching rows are updated, not duplicated.
      </p>

      <label
        htmlFor="yoycol-csv-upload"
        className="mt-4 flex cursor-pointer items-center justify-center rounded-md border border-dashed border-gray-300 px-4 py-6 text-sm text-gray-600 hover:border-gray-400 hover:bg-gray-50"
      >
        {status === 'uploading' ? 'Uploading…' : 'Choose CSV file'}
        <input
          id="yoycol-csv-upload"
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileChange}
          disabled={status === 'uploading'}
        />
      </label>

      {fileName && status !== 'idle' && (
        <p className="mt-2 text-xs text-gray-400">{fileName}</p>
      )}

      {status === 'done' && result && (
        <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
          {result.message || `Saved ${result.rowsSaved} rows.`}
        </div>
      )}

      {status === 'error' && result && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {result.error}
        </div>
      )}
    </div>
  );
}
